import React, { useCallback, useMemo } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import { useTheme } from "@mui/material/styles";
import ShoppingBagIcon from "@mui/icons-material/ShoppingBag";
import FilterListIcon from "@mui/icons-material/FilterList";
import SearchIcon from "@mui/icons-material/Search";
import { useModalRouter } from "@/hooks/useModalRouter";
import { useMenu, usePrice } from "@/hooks";

interface FloatingCartsProps {
  itemCount: number;
  totalSum: number;
  isEmpty: boolean;
}

/**
 * Floating Carts Component
 * Positioned bottom right with 3 buttons: favorites, share, and cart
 */
export const FloatingCarts: React.FC<FloatingCartsProps> = React.memo(
  ({ itemCount, totalSum, isEmpty }) => {
    const theme = useTheme();
    const { navigateToModal } = useModalRouter();
    const { activeFilters } = useMenu();
    const price = usePrice();

    const getPositionText = useCallback((count: number) => {
      if (count === 1) return "позиция";
      if (count >= 2 && count <= 4) return "позиции";
      return "позиций";
    }, []);

    const handleCartClick = useCallback(() => {
      navigateToModal("order");
    }, [navigateToModal]);

    const handleFilterClick = useCallback(() => {
      navigateToModal("filter");
    }, [navigateToModal]);

    const handleSearchClick = useCallback(() => {
      navigateToModal("search");
    }, [navigateToModal]);

    const buttonBaseStyles = useMemo(
      () => ({
        backgroundColor:
          theme.palette.mode === "dark"
            ? "rgba(255, 255, 255, 0.9)"
            : "rgba(23, 24, 24, 0.9)",
        backdropFilter: "blur(8px)",
        height: 52,
        borderRadius: theme.spacing(2),
        cursor: "pointer",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        px: 2,
        transition: theme.transitions.create(
          ["background-color", "transform"],
          {
            duration: theme.transitions.duration.short,
          }
        ),
        "&:hover": {
          backgroundColor:
            theme.palette.mode === "dark"
              ? "rgba(255, 255, 255, 0.95)"
              : "rgba(23, 24, 24, 0.95)",
          transform: "translateY(-2px)",
        },
      }),
      [theme.palette.mode]
    );

    const iconStyles = useMemo(
      () => ({
        color:
          theme.palette.mode === "dark"
            ? "rgba(0, 0, 0, 0.6)"
            : "rgba(255, 255, 255, 0.6)",
        fontSize: theme.spacing(2.5),
      }),
      [theme.palette.mode]
    );

    // Подсчет активных фильтров
    const activeFiltersCount = useMemo(() => {
      let count = 0;
      if (activeFilters.tagsInclude?.length) {
        count += activeFilters.tagsInclude.length;
      }
      if (activeFilters.tagsExclude?.length) {
        count += activeFilters.tagsExclude.length;
      }
      if (activeFilters.sortByPrice) {
        count += 1;
      }
      return count;
    }, [activeFilters]);

    return (
      <Box
        sx={{
          position: "fixed",
          bottom: theme.spacing(2.5),
          right: theme.spacing(2.5),
          left: theme.spacing(2.5),
          zIndex: 1000,
          display: "flex",
          gap: theme.spacing(1.5),
          alignItems: "center",
        }}
      >
        {/* Left Buttons Container */}
        <Box
          sx={{
            display: "flex",
            flex: 1,
            gap: theme.spacing(1),
          }}
        >
          {/* Filter Button */}
          <Box sx={{ position: "relative" }}>
            <IconButton
              onClick={handleFilterClick}
              sx={{
                ...buttonBaseStyles,
                width: 52,
              }}
            >
              <FilterListIcon sx={iconStyles} />
              {activeFiltersCount > 0 && (
                <Box
                  sx={{
                    position: "absolute",
                    top: "50%",
                    left: "50%",
                    transform: "translate(-50%, -50%)",
                    display: "flex",
                    minWidth: "30px",
                    padding: "5px 10px",
                    flexDirection: "column",
                    justifyContent: "center",
                    alignItems: "center",
                    gap: "10px",
                    borderRadius: "9px",
                    background: "#E12222",
                    zIndex: 1,
                  }}
                >
                  <Typography
                    sx={{
                      color: "#FFF",
                      fontSize: "15px",
                      fontStyle: "normal",
                      fontWeight: 500,
                      lineHeight: "20px",
                    }}
                  >
                    {activeFiltersCount}
                  </Typography>
                </Box>
              )}
            </IconButton>
          </Box>

          {/* Search Button */}
          <IconButton
            onClick={handleSearchClick}
            sx={{
              ...buttonBaseStyles,
              width: 52,
            }}
          >
            <SearchIcon sx={iconStyles} />
          </IconButton>
        </Box>

        {/* Cart Button (Right) */}
        {!isEmpty && (
          <Box
            sx={{
              ...buttonBaseStyles,
            }}
            onClick={handleCartClick}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: theme.spacing(1.25),
              }}
            >
              <ShoppingBagIcon sx={iconStyles} />
              <Typography
                sx={{
                  fontSize: "15px",
                  lineHeight: "20px",
                  color:
                    theme.palette.mode === "dark"
                      ? theme.palette.common.black
                      : theme.palette.common.white,
                  fontWeight: 400,
                  whiteSpace: "nowrap",
                }}
              >
                {itemCount} {getPositionText(itemCount)}{" "}
                <Box
                  component="span"
                  sx={{
                    color:
                      theme.palette.mode === "dark"
                        ? "rgba(0, 0, 0, 0.41)"
                        : "rgba(255, 255, 255, 0.41)",
                    fontWeight: 400,
                  }}
                >
                  ×
                </Box>{" "}
                <Box
                  component="span"
                  sx={{
                    fontWeight: 500,
                  }}
                >
                  {price.formatSimple(totalSum)}
                </Box>
              </Typography>
            </Box>
          </Box>
        )}
      </Box>
    );
  }
);

FloatingCarts.displayName = "FloatingCarts";
